import { Link } from "react-router-dom";
import { Icon } from "./Icon";

// ─────────────────────────────────────────────────────────────────────────────
//  OSJ — the mark.
//
//  A themed tile with a buzzer glyph, next to the wordmark. The tile is the
//  board; the buzzer is what you do to it. It scales from a 16px favicon to a
//  projector header without redrawing.
// ─────────────────────────────────────────────────────────────────────────────

export function LogoMark({ size = 32, className = "" }: { size?: number; className?: string }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-[28%] bg-accent text-onAccent shrink-0 ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <Icon name="buzzer" size={size * 0.62} strokeWidth={2} />
    </span>
  );
}

export function Wordmark({
  className = "",
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span className={`type-display ${className}`} style={style}>
      OSJ
    </span>
  );
}

/**
 * Logo lockup. `to` makes it a link home — every screen except the showcase,
 * where nothing should be clickable.
 */
export function Logo({
  size = 32,
  to = "/",
  showWordmark = true,
  className = "",
}: {
  size?: number;
  to?: string | null;
  showWordmark?: boolean;
  className?: string;
}) {
  const inner = (
    <>
      <LogoMark size={size} />
      {showWordmark && (
        <Wordmark className="text-accentLight" style={{ fontSize: size * 0.72 }} />
      )}
    </>
  );

  const shared = `inline-flex items-center gap-2.5 ${className}`;
  if (!to) return <span className={shared}>{inner}</span>;
  return (
    <Link to={to} className={`${shared} hover:opacity-85 transition-opacity`} aria-label="OSJ — home">
      {inner}
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Attribution
// ─────────────────────────────────────────────────────────────────────────────

const PORTFOLIO = "https://youniss.dev";

/** Footer credit for the screens a person browses rather than plays on. */
export function BuiltBy({ className = "" }: { className?: string }) {
  return (
    <p className={`text-sm text-ink/40 ${className}`}>
      Built by{" "}
      <a
        href={PORTFOLIO}
        target="_blank"
        rel="noreferrer author"
        className="text-ink/70 hover:text-accentLight underline underline-offset-4 decoration-ink/25 hover:decoration-accentLight transition-colors"
      >
        Youniss
      </a>
      <span className="text-ink/25"> · </span>
      <a
        href={PORTFOLIO}
        target="_blank"
        rel="noreferrer"
        className="text-ink/50 hover:text-accentLight transition-colors"
      >
        youniss.dev
      </a>
    </p>
  );
}

/**
 * Sign-off for the showcase. That screen is on a TV for an hour, so the credit
 * earns its place — but it must never compete with the question, hence the low
 * contrast and the corner.
 */
export function ShowcaseCredit() {
  return (
    <div className="pointer-events-none select-none absolute bottom-3 right-4 flex items-center gap-1.5 text-ink/25 text-xs">
      <LogoMark size={14} className="opacity-60" />
      <span className="tracking-wide">osj · youniss.dev</span>
    </div>
  );
}
